import { useState } from "react";
import useReveal from "@/hooks/useReveal";
import { useLang } from "@/i18n/LangContext";
import GlassSlider from "@/components/site/GlassSlider";
import CostEstimator from "@/components/site/CostEstimator";

// Final order: Standard, Premium
const PRODUCT_IDS = ["standard", "premium"];

// Base price (HUF per m²). Matches the "Ár / Price" row in the spec sheets.
const PRICES_PER_M2 = {
  standard: 55000,
  premium: 75000,
};

const SLIDER_SOURCES = {
  standard: {
    clearSrc: "/products/standard_reverse_clear.png",
    opaqueSrc: "/products/standard_reverse_opaque.png",
  },
  premium: {
    clearSrc: "/products/villa_clear.png",
    opaqueSrc: "/products/villa_opaque.png",
  },
};

function ProductCard({ id, index, onInquire }) {
  const { t } = useLang();
  const { ref, className } = useReveal();
  const p = t(`products.items.${id}`);
  const sources = SLIDER_SOURCES[id];
  // Alternate image/text side on md+ for visual rhythm.
  const reverseLayout = index % 2 === 1;

  return (
    <article
      ref={ref}
      data-testid={`product-${id}`}
      className={`${className} grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div className={reverseLayout ? "md:order-2" : ""}>
        <GlassSlider
          clearSrc={sources.clearSrc}
          opaqueSrc={sources.opaqueSrc}
          initial={50}
          reverse={id === "standard" || id === "premium"}
          testId={`product-${id}-slider`}
        />
      </div>

      <div className={reverseLayout ? "md:order-1" : ""}>
        <h3 className="font-display font-light tracking-tight text-2xl md:text-3xl lg:text-[2rem] text-[#1D1D1F] leading-[1.1]">
          {p.name}
        </h3>
        <p className="mt-3 font-body text-[14px] md:text-[15px] text-[#1D1D1F]/70 leading-relaxed max-w-md">
          {p.description}
        </p>
        <dl
          data-testid={`product-${id}-specs`}
          className="mt-6 max-w-md border-t border-[#1D1D1F]/10"
        >
          {p.specs.map((s) => (
            <div
              key={s.label}
              className="flex items-baseline justify-between gap-6 border-b border-[#1D1D1F]/10 py-2"
            >
              <dt className="text-[12px] font-body text-[#1D1D1F]/55 tracking-tight">
                {s.label}
              </dt>
              <dd className="text-[12px] font-body font-medium text-[#1D1D1F] text-right whitespace-nowrap">
                {s.value}
              </dd>
            </div>
          ))}
        </dl>
        <button
          data-testid={`product-inquire-${id}`}
          onClick={() => onInquire({ id, name: p.name, pricePerM2: PRICES_PER_M2[id] })}
          className="mt-6 inline-flex items-center justify-center bg-[#1D1D1F] hover:bg-black text-white text-[12px] font-body font-medium rounded-full px-[16px] py-[8px] transition-colors"
        >
          {t("products.cta")}
        </button>
      </div>
    </article>
  );
}

export default function Products() {
  const { t } = useLang();
  const [openProduct, setOpenProduct] = useState(null);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="pt-20 md:pt-24 lg:pt-28 pb-10 md:pb-12 bg-[#FAFAFA]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl mb-12 md:mb-16">
          <h2 className="font-display font-light tracking-tight text-3xl md:text-4xl lg:text-5xl text-[#1D1D1F] leading-[1.05]">
            {t("products.overline")}
          </h2>
        </div>

        <div className="flex flex-col gap-16 md:gap-20 lg:gap-24">
          {PRODUCT_IDS.map((id, i) => (
            <ProductCard
              key={id}
              id={id}
              index={i}
              onInquire={setOpenProduct}
            />
          ))}
        </div>
      </div>

      <CostEstimator
        open={!!openProduct}
        product={openProduct}
        onClose={() => setOpenProduct(null)}
      />
    </section>
  );
}
